import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';

/*
 * The one way to ask "are you sure?".
 *
 * Replaces window.confirm, which blocks the whole tab, cannot be styled, cannot
 * be tested without stubbing a global, and — because Chrome lets a user suppress
 * further dialogs from a page — can silently start returning false for the rest
 * of the session.
 *
 * Deliberately unopinionated about what happens after `onConfirm`: every caller
 * already has its own in-flight and error surface next to the control that
 * opened this, so the dialog closes and gets out of the way rather than growing
 * a second place for the same error to appear.
 */
export type ConfirmDialogProps = {
  open: boolean;
  title: ReactNode;
  /** The consequence, in a sentence. This is what the user actually reads. */
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive or irreversible. */
  tone?: 'danger' | 'primary';
  /**
   * The AWS-style "type X to confirm" gate: when set, the confirm button
   * stays disabled until the user types this exact text into an input
   * rendered inside the dialog. Reserved for the actions a misclick on
   * `onConfirm` alone would make too easy — account deletion, not a token
   * revoke.
   */
  confirmText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
};

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  confirmText,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  // Cleared on every open, not just on unmount -- the dialog is a single
  // persistent element toggled by `open`, so without this a confirmed-then-
  // reopened dialog would start pre-filled with the last correct answer.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const guardFailed = confirmText !== undefined && typed !== confirmText;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          {/* Dismiss first in DOM order: Modal focuses its close button on open
              and Tab walks forward from there, so the destructive control is
              never the thing a keyboard user reaches by reflex. */}
          <Button onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={tone} onClick={onConfirm} disabled={guardFailed}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {confirmText !== undefined && (
        <Input
          label={
            <>
              Type <strong>{confirmText}</strong> to confirm
            </>
          }
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      )}
    </Modal>
  );
}
